import React from 'react';
import { FOCUS_RING_INSET } from './focusRing';

export interface ClampTextProps {
    children: React.ReactNode;
    lines?: number;
    expandOnHover?: boolean;
    className?: string;
}

/**
 * Text clamped to `lines`, revealing the full text on hover / keyboard focus when
 * `expandOnHover`. Pure CSS. The expanded state caps at 1000px, so this is for short blurbs.
 *
 * `line-height` is `1.5em`, NOT the bare `1.5`, and that unit is what makes the cap exact: a
 * `<number>` inherits as a FACTOR, so each descendant re-resolves it against its own
 * font-size, while `max-height: calc(1.5em * lines)` resolves against THIS element's. With
 * the bare number they disagreed — 14px text in a 13px row gave 21px line boxes against a
 * 19.5px allowance and the last line was sliced. As a `<length>` both share one basis.
 *
 * The cap also bounds block children, which `-webkit-line-clamp` counts as one "line" each —
 * so a MarkdownBlock in `block` variant relies on it. (Prefer `variant="flow"` there.)
 */
const ClampText: React.FC<ClampTextProps> = ({
    children,
    lines = 3,
    expandOnHover = true,
    className = '',
}) => (
    <div
        className={[
            'w-full overflow-hidden leading-[1.5em] [display:-webkit-box] [-webkit-box-orient:vertical]',
            '[-webkit-line-clamp:var(--malachite-clamp-lines)]',
            'max-h-[calc(1.5em*var(--malachite-clamp-lines))]',
            'transition-[max-height] duration-300 ease-[var(--malachite-ease-standard)] motion-reduce:transition-none',
            expandOnHover
                ? // Inset ring: the element is `overflow-hidden`, so an outset outline is clipped.
                  `${FOCUS_RING_INSET} hover:[-webkit-line-clamp:unset] focus-visible:[-webkit-line-clamp:unset] hover:max-h-[1000px] focus-visible:max-h-[1000px]`
                : '',
            className,
        ]
            .filter(Boolean)
            .join(' ')}
        style={{ '--malachite-clamp-lines': lines } as React.CSSProperties}
        tabIndex={expandOnHover ? 0 : undefined}
    >
        {children}
    </div>
);

export default ClampText;
